import React, { useEffect, useState } from 'react';
import { Upload, Download, Trash2, FileText } from 'lucide-react';
import { api } from '../../lib/api';
import type { StaffHandbook } from '../../types';
import { formatDate, formatFileSize } from '../../lib/format';
import { useUpload } from '../../lib/useUpload';
import { Spinner, EmptyState, Alert, ConfirmDialog, UploadProgress, inputClass, labelClass, primaryBtn } from '../../components/ui';

export default function StaffHandbooks() {
  const [handbooks, setHandbooks] = useState<StaffHandbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const upload = useUpload();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<StaffHandbook[]>('/handbooks')
      .then(setHandbooks)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    upload.reset();
    setMessage('');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title || 'Staff Handbook');
      await upload.run('/handbooks', fd);
      setTitle('');
      setFile(null);
      setMessage('Staff handbook uploaded. It is now available to all staff in the Staff Portal.');
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/handbooks/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Staff Handbook</h1>
          <p className="text-sm text-gray-500 mt-1">
            Upload a company handbook (PDF or Word). Staff can download the latest version from the Staff Portal.
          </p>
        </div>
      </div>

      {message && <div className="px-4 py-3 rounded-lg bg-green-50 text-green-800 text-sm">{message}</div>}
      {error && <Alert>{error}</Alert>}

      {/* Upload form */}
      <form onSubmit={handleUpload} className="bg-white rounded-lg shadow-lg p-6 max-w-2xl">
        <h2 className="font-semibold mb-4 flex items-center">
          <Upload className="h-5 w-5 text-gray-500 mr-2" /> Publish New Handbook
        </h2>
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Title</label>
            <input
              className={inputClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Staff Handbook"
            />
          </div>
          <div>
            <label className={labelClass}>Document (PDF, Word)</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf"
              className={inputClass}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file && (
              <p className="text-xs text-gray-500 mt-2 flex items-center min-w-0">
                <FileText className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                <span className="truncate min-w-0">{file.name}</span>
                <span className="shrink-0 ml-1">· {formatFileSize(file.size)}</span>
              </p>
            )}
          </div>
          <UploadProgress
            percent={upload.percent}
            phase={upload.phase}
            error={upload.error}
            onCancel={upload.cancel}
          />
          <div className="flex justify-end">
            <button type="submit" disabled={upload.busy || !file} className={primaryBtn}>
              <Upload className="h-4 w-4" />
              {upload.phase === 'uploading'
                ? `Uploading ${upload.percent}%`
                : upload.phase === 'processing' ? 'Processing...' : 'Publish Handbook'}
            </button>
          </div>
        </div>
      </form>

      {/* List */}
      <div>
        <h2 className="font-semibold mb-3">Published Versions</h2>
        {loading ? (
          <Spinner label="Loading handbooks..." />
        ) : handbooks.length === 0 ? (
          <EmptyState message="No handbooks published yet." />
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3">Uploaded By</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {handbooks.map((h, i) => (
                  <tr key={h.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      {h.title}
                      {i === 0 && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/20 text-primary-dark text-[11px] font-semibold">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{h.filename}</td>
                    <td className="px-4 py-3">{h.uploaded_by_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{formatFileSize(h.size)}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(h.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <a
                          href={h.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 text-xs hover:bg-gray-50"
                        >
                          <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                        </a>
                        <button onClick={() => setDeleteId(h.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Handbook"
        message="Are you sure you want to delete this staff handbook? Staff will no longer be able to download it."
      />
    </div>
  );
}
